import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../../ui/Modal';
import DocumentViewer from '../../../shared/DocumentViewer';
import PrepCommentRail from './PrepCommentRail';
import meetingsAPI from '../../../../services/meetingsApi';
import { PrepComment } from '../../../../types/meeting';
import { isPdfFile } from '../../../../types/deck';
import { MeetingFile } from './MeetingMaterialsPanel';

interface MaterialReaderPanelProps {
  meetingId: string;
  /** The material to read. Null closes the reader. */
  file: MeetingFile | null;
  onClose: () => void;
  /** Fallback for a material that cannot be rendered inline. */
  onDownload: (file: MeetingFile) => void;
  /** Lets the materials list keep its per-material note count current. */
  onCommentsChanged?: (fileId: string, comments: PrepComment[]) => void;
}

/**
 * Reads a meeting material on screen, alongside the reader's own prep notes —
 * the point of the feature being that preparation happens here rather than in a
 * downloads folder.
 *
 * Only PDFs render inline today. Anything else falls back to download, and its
 * notes are document-level because there is no page to pin them to.
 */
const MaterialReaderPanel: React.FC<MaterialReaderPanelProps> = ({
  meetingId,
  file,
  onClose,
  onDownload,
  onCommentsChanged,
}) => {
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [comments, setComments] = useState<PrepComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileId = file?.file_id ?? null;
  const renderable = isPdfFile(file?.filename);

  // Reset per-material view state, so opening a second document does not
  // inherit the first one's page or notes.
  useEffect(() => {
    setPage(1);
    setNumPages(null);
    setComments([]);
    setError(null);
  }, [fileId]);

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    setLoading(true);
    meetingsAPI
      .listPrepComments(meetingId, fileId)
      .then((loaded) => {
        if (cancelled) return;
        setComments(loaded);
        onCommentsChanged?.(fileId, loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load your notes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // onCommentsChanged is reported, not depended on: including it would refetch
    // whenever the parent re-renders with a fresh callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, fileId]);

  // Referentially stable: DocumentViewer is memoized.
  const handleNumPages = useCallback((_fileId: string, n: number) => setNumPages(n), []);

  const apply = (next: PrepComment[]) => {
    setComments(next);
    if (fileId) onCommentsChanged?.(fileId, next);
  };

  const handleCreate = async (body: string) => {
    if (!fileId) return;
    setError(null);
    try {
      const created = await meetingsAPI.createPrepComment(meetingId, {
        file_id: fileId,
        page_number: renderable ? page : null,
        is_document_level: !renderable,
        body,
      });
      apply([...comments, created]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save your note');
    }
  };

  const handleUpdate = async (comment: PrepComment, body: string) => {
    setError(null);
    try {
      const updated = await meetingsAPI.updatePrepComment(meetingId, comment.id, body);
      apply(comments.map((c) => (c.id === comment.id ? updated : c)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update your note');
    }
  };

  const handleDelete = async (comment: PrepComment) => {
    setError(null);
    try {
      await meetingsAPI.deletePrepComment(meetingId, comment.id);
      apply(comments.filter((c) => c.id !== comment.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete your note');
    }
  };

  const goToPage = (target: number) => {
    if (!renderable) return;
    const highest = numPages ?? target;
    setPage(Math.min(Math.max(1, target), highest));
  };

  return (
    <Modal
      isOpen={!!file}
      onClose={onClose}
      title={file?.filename || 'Material'}
      maxWidth="1200px"
      className="material-reader-modal"
    >
      <div className="material-reader">
        <div className="material-reader-doc">
          {renderable ? (
            <>
              <div className="material-reader-toolbar">
                <button
                  className="meeting-btn meeting-btn-secondary meeting-btn-sm"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                  aria-label="Previous page"
                >
                  ← Previous
                </button>
                <span className="material-reader-counter" role="status" aria-live="polite">
                  {numPages ? `Page ${page} of ${numPages}` : `Page ${page}…`}
                </span>
                <button
                  className="meeting-btn meeting-btn-secondary meeting-btn-sm"
                  disabled={!!numPages && page >= numPages}
                  onClick={() => goToPage(page + 1)}
                  aria-label="Next page"
                >
                  Next →
                </button>
              </div>
              <DocumentViewer
                className="material-reader-canvas"
                placeholderClassName="material-reader-placeholder"
                fileId={fileId}
                page={page}
                onNumPages={handleNumPages}
                loadingLabel="Loading document…"
                errorLabel="Couldn’t display this document."
              />
            </>
          ) : (
            <div className="material-reader-placeholder">
              <div>This file type can’t be read on screen yet.</div>
              {file && (
                <button
                  className="meeting-btn meeting-btn-secondary meeting-btn-sm"
                  onClick={() => onDownload(file)}
                >
                  Download to read
                </button>
              )}
              <div className="material-reader-fallback-hint">
                Notes on it are saved against the whole document.
              </div>
            </div>
          )}
        </div>

        <PrepCommentRail
          comments={comments}
          page={page}
          isDocumentLevel={!renderable}
          busy={loading}
          error={error}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onJumpToPage={goToPage}
        />
      </div>
    </Modal>
  );
};

export default MaterialReaderPanel;
